import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { materialPropertyPairsSearchBlob } from "../helpers/materialPropertyPairs";
import { isMaterialUnitKg, normalizeMaterialUnitToCode } from "../helpers/materialUnits";
import { effectiveKgPerLinearMeterForMaterial } from "../lib/materialFabricKg";
import axios from "axios";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { ArrowDown, ArrowUp, Copy, Eraser, History, Pencil, Trash2 } from "lucide-react";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import HubChangeLogModal from "../components/HubChangeLogModal";
import {
    TECH_CARD_AUTOCOMPLETE_COMPONENTS_PROPS,
    TECH_CARD_AUTOCOMPLETE_LISTBOX_SX,
    TECH_CARD_MUI_FIELD_SX,
} from "../helpers/muiTechCardEditorFieldSx";
import { parseProductionVariantSearchResponse } from "../helpers/utils";
import {
    effectiveStockUnitPriceUAH,
} from "../lib/materialStockUnitPriceUAH";

const API_PROD = "/api/production";

const TECH_CARD_READINESS_BADGE_CLASS = "tech-card-readiness-badge";

/** Текст для користувача замість «Request failed with status code 500». */
function formatTechCardsAxiosError(e, fallback = "Помилка запиту") {
    const d = e?.response?.data;
    const msg =
        (typeof d?.message === "string" && d.message.trim()) ||
        (typeof d?.error === "string" && d.error.trim());
    if (msg) {
        return msg;
    }
    const st = e?.response?.status;
    if (st === 500 || st === 502 || st === 503) {
        return "Тимчасова помилка сервера (500). Зачекайте хвилину або оновіть сторінку.";
    }
    if (st === 401 || st === 403) {
        return "Немає доступу до цієї операції.";
    }
    if (st === 404) {
        return "Дані не знайдено.";
    }
    const raw = String(e?.message || "").trim();
    if (/^Request failed with status code\s*\d+/i.test(raw) || raw === "Network Error") {
        return "Не вдалося звʼязатися з сервером. Перевірте зʼєднання або спробуйте пізніше.";
    }
    return raw || fallback;
}

function normalizeColorKeyClient(v) {
    return String(v || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

/** Чи вже є збережена техкарта для product_id + кольору (як на сервері). */
function variantOfferHasTechCard(v, coverageSet) {
    if (!v?.colorValue) {
        return false;
    }
    const ck = normalizeColorKeyClient(v.colorValue);
    if (!ck) {
        return false;
    }
    const pid = Number(v.product_id);
    if (!Number.isFinite(pid) || pid <= 0) {
        return false;
    }
    return coverageSet.has(`${pid}|${ck}`);
}

/** Вага 1 м.п. (кг): з effectiveKgPerLinearMeterForMaterial (властивість матеріалу / тип). */
function effectiveKgPm(m) {
    if (!m) return undefined;
    const n = effectiveKgPerLinearMeterForMaterial(m);
    if (Number.isFinite(n) && n > 0) return n;
    return undefined;
}

/** Норма «пог. м → кг» — якщо одиниця складу «кг». */
function materialNeedsKgPerLinearMeter(m) {
    return isMaterialUnitKg(m?.unit);
}

function parseTechCardQtyStr(s) {
    if (s === undefined || s === null || String(s).trim() === "") {
        return NaN;
    }
    return Number(String(s).trim().replace(",", "."));
}

function roundMoneyDisplay2(n) {
    return Math.round(Number(n) * 100) / 100;
}

/**
 * Одиниці складу (кг, м, шт…) на 1 готовий виріб за рядком форми редактора
 * (ті самі правила, що `stockUnitsPerGarmentForTechLine` у production.routes.js).
 */
function stockUnitsPerGarmentForEditorLine(mat, editorLine) {
    const per = parseTechCardQtyStr(editorLine.qtyPerUnit);
    if (!Number.isFinite(per) || per < 0) {
        return null;
    }
    const normUnit = editorLine.normUnit === "linear_m" ? "linear_m" : "stock";
    if (normUnit === "linear_m") {
        const kglm = effectiveKgPerLinearMeterForMaterial(mat);
        if (!Number.isFinite(kglm) || kglm <= 0) {
            return null;
        }
        return per * kglm;
    }
    return per;
}

/** Вартість рядка на 1 виріб у ₴ (ціна за одиницю складу або ₴/м при базі «бобіна»). */
function techCardEditorLineCostUAH(mat, editorLine) {
    const units = stockUnitsPerGarmentForEditorLine(mat, editorLine);
    if (units == null || !(units > 0)) {
        return null;
    }
    const p = effectiveStockUnitPriceUAH(mat);
    if (p == null || !Number.isFinite(p) || p < 0) {
        return null;
    }
    return units * p;
}

function formatMoneyUa2Fixed(n) {
    return `${roundMoneyDisplay2(n).toFixed(2)} ₴`;
}

function emptyLine() {
    return {
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        materialId: "",
        normUnit: "stock",
        qtyPerUnit: "",
        note: "",
    };
}

/** Стабільне порівняння shared id для «є зміни / ні». */
function normalizeSharedIdsForCompare(text) {
    return String(text || "")
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .join(",");
}

function serializeTechCardEditorState(lines, sharedProductIdsText, readinessStatusValue) {
    const normalizedLines = (lines || []).map((L) => ({
        materialId: String(L.materialId || "").trim(),
        normUnit: L.normUnit === "linear_m" ? "linear_m" : "stock",
        qtyPerUnit: String(L.qtyPerUnit ?? "").trim(),
        note: String(L.note ?? "").trim(),
    }));
    const readiness =
        readinessStatusValue === "complete" || readinessStatusValue === "needs_work"
            ? readinessStatusValue
            : "";
    return JSON.stringify({
        lines: normalizedLines,
        shared: normalizeSharedIdsForCompare(sharedProductIdsText),
        readiness,
    });
}

/** Рядок «тип / колір» для поля автокомпліту та сортування (без од. виміру; постачальник — у renderOption). */
function materialAutocompleteHeadline(m) {
    if (!m) {
        return "";
    }
    const t = m.typeId?.name || "—";
    const color =
        m.color != null && String(m.color).trim() !== "" ? String(m.color).trim() : "";
    return color ? `${t} / ${color}` : t;
}

/** Список для автокомпліту; уже обраний рядок лишається видимим навіть поза фільтром; порядок — за алфавітом (uk). */
function materialOptionsForProductEditor(allMaterials, _productIdStr, matRow) {
    let list = (allMaterials || []).slice();
    if (matRow && !list.some((m) => String(m._id) === String(matRow._id))) {
        list = [...list, matRow];
    }
    return [...list].sort((a, b) =>
        materialAutocompleteHeadline(a).localeCompare(materialAutocompleteHeadline(b), "uk", {
            sensitivity: "base",
        })
    );
}

/** Інша техкарта того самого product_id з нормами (інший колір). */
function pickBestSiblingSourceCard(cards, productIdStr, targetColorKeyNormalized) {
    const pid = String(productIdStr || "");
    if (!pid) return null;
    const candidates = (cards || []).filter((c) => {
        if (String(c.product_id) !== pid) return false;
        const ck = normalizeColorKeyClient(c.colorValue || c.colorKey || "");
        if (!ck || ck === targetColorKeyNormalized) return false;
        return (c.lines || []).length > 0;
    });
    if (!candidates.length) return null;
    candidates.sort((a, b) => {
        const la = (a.lines || []).length;
        const lb = (b.lines || []).length;
        if (lb !== la) return lb - la;
        const ta = new Date(a.updatedAt || 0).getTime();
        const tb = new Date(b.updatedAt || 0).getTime();
        return tb - ta;
    });
    return candidates[0];
}

function mapSiblingSourceLinesToEditorLines(srcLines, targetColorDisplay, materials) {
    const targetColor = String(targetColorDisplay || "").trim();
    return (srcLines || []).map((L) => {
        const srcMat = materials.find((m) => String(m._id) === String(L.materialId));
        let newMaterialId = "";
        if (srcMat) {
            const tid = String(srcMat.typeId?._id || srcMat.typeId);
            const hit = materials.find(
                (m) =>
                    String(m.typeId?._id || m.typeId) === tid &&
                    normalizeColorKeyClient(m.color) === normalizeColorKeyClient(targetColor)
            );
            if (hit) newMaterialId = String(hit._id);
        }
        return {
            key: `${Date.now()}-${Math.random()}-${L.materialId}`,
            materialId: newMaterialId,
            normUnit: L.normUnit === "linear_m" ? "linear_m" : "stock",
            qtyPerUnit: L.qtyPerUnit != null ? String(L.qtyPerUnit) : "",
            note: L.note || "",
        };
    });
}

/** Рядки з API копії техкарти — ті самі materialId, без переприв’язки під колір offer (користувач міняє вручну за потреби). */
function mapCopySourceApiLinesToEditorLinesPreservingMaterials(srcLines) {
    return (srcLines || []).map((L) => ({
        key: `${Date.now()}-${Math.random()}-${L.materialId}`,
        materialId: String(L.materialId || ""),
        normUnit: L.normUnit === "linear_m" ? "linear_m" : "stock",
        qtyPerUnit: L.qtyPerUnit != null ? String(L.qtyPerUnit) : "",
        note: L.note || "",
    }));
}

/** Порядок статусу для сортування (менше — «краще» / вище при зростанні). */
function techCardReadinessRank(c) {
    const rs = c?.readinessStatus;
    if (rs === "complete") return 0;
    if (rs === "needs_work") return 1;
    return 2;
}

/**
 * Порівняння двох техкарт для таблиці списку; повертає <0 якщо a перед b у порядку asc.
 * @param {'name'|'color'|'materials'|'cost'|'status'} sortKey
 */
function compareTechCardsList(a, b, sortKey, dirAsc) {
    let cmp = 0;
    switch (sortKey) {
        case "name":
            cmp = String(a.name || "").localeCompare(String(b.name || ""), "uk", {
                sensitivity: "base",
            });
            break;
        case "color":
            cmp = String(a.colorValue || a.colorKey || "").localeCompare(
                String(b.colorValue || b.colorKey || ""),
                "uk",
                { sensitivity: "base" }
            );
            break;
        case "materials":
            cmp = String(a.materialsSummary || "").localeCompare(
                String(b.materialsSummary || ""),
                "uk",
                { sensitivity: "base" }
            );
            break;
        case "cost": {
            const na = Number(a.materialsCostUAH);
            const nb = Number(b.materialsCostUAH);
            const fa = Number.isFinite(na) ? na : null;
            const fb = Number.isFinite(nb) ? nb : null;
            if (fa == null && fb == null) cmp = 0;
            else if (fa == null) return 1;
            else if (fb == null) return -1;
            else cmp = dirAsc ? fa - fb : fb - fa;
            break;
        }
        case "status":
            cmp = techCardReadinessRank(a) - techCardReadinessRank(b);
            break;
        default:
            return 0;
    }
    if (cmp !== 0) return dirAsc ? cmp : -cmp;
    const pa = String(a.product_id ?? "");
    const pb = String(b.product_id ?? "");
    let tie = pa.localeCompare(pb, "uk", { numeric: true });
    if (tie !== 0) return dirAsc ? tie : -tie;
    tie = String(a.colorKey || "").localeCompare(String(b.colorKey || ""), "uk", {
        sensitivity: "base",
    });
    return dirAsc ? tie : -tie;
}

function techCardReadinessBadge(c) {
    const rs = c?.readinessStatus;
    if (rs === "complete") {
        return (
            <Badge bg="success" className={TECH_CARD_READINESS_BADGE_CLASS}>
                Готова
            </Badge>
        );
    }
    if (rs === "needs_work") {
        return (
            <Badge bg="warning" text="dark" className={TECH_CARD_READINESS_BADGE_CLASS}>
                Потрібно доповнити
            </Badge>
        );
    }
    return (
        <Badge bg="secondary" className={TECH_CARD_READINESS_BADGE_CLASS}>
            Не вказано
        </Badge>
    );
}

function borrowKgGsmFromSourceLinesToMaterials(prev, srcLines, newLines) {
    const next = prev.map((m) => ({ ...m }));
    for (let i = 0; i < srcLines.length; i++) {
        const L = srcLines[i];
        const srcMat = prev.find((mm) => String(mm._id) === String(L.materialId));
        const newId = newLines[i]?.materialId;
        if (!newId || !srcMat) continue;
        const idx = next.findIndex((mm) => String(mm._id) === newId);
        if (idx < 0) continue;
        const m = next[idx];
        let upd = { ...m };
        let changed = false;
        const existingG = Number(upd.fabricGsm);
        if (!(Number.isFinite(existingG) && existingG > 0)) {
            const gSrc = Number(srcMat.fabricGsm);
            if (Number.isFinite(gSrc) && gSrc > 0) {
                upd.fabricGsm = gSrc;
                changed = true;
            }
        }
        if (changed) next[idx] = upd;
    }
    return next;
}

export default function TechCardsPage() {
    const [materials, setMaterials] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [cards, setCards] = useState([]);
    /** Сортування таблиці списку техкарт за кліком на заголовок */
    const [listSort, setListSort] = useState({ key: null, dir: "asc" });
    const [filterQ, setFilterQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [okMsg, setOkMsg] = useState(null);

    const [selectedProductId, setSelectedProductId] = useState("");
    const [colorValue, setColorValue] = useState("");
    const [sampleProduct, setSampleProduct] = useState(null);
    const [cardLoaded, setCardLoaded] = useState(null);

    const cardsRef = useRef([]);
    const materialsRef = useRef([]);
    const editorTargetRef = useRef({ productId: "", colorValue: "" });
    /**
     * Копія техкарти: sourceApiLines + shared/readiness; після вибору нового offer рядки лишаються з тими ж materialId.
     */
    const techCardCopySessionRef = useRef(null);
    /** Поки true і ключ збігається з джерелом — cardLoaded лишається null (форма як копія, не «редагування»). */
    const techCardCopyAwaitingNewOfferRef = useRef(false);
    const techCardCopySourceRowKeyRef = useRef("");
    /** У режимі копії — обмежити пошук offer лише варіантами цього product_id (джерело копії). */
    const techCardCopySourceProductIdRef = useRef("");

    const [variantSearch, setVariantSearch] = useState("");
    const [variantSuggestRaw, setVariantSuggestRaw] = useState([]);
    const [variantLoading, setVariantLoading] = useState(false);
    const [variantOfferOpen, setVariantOfferOpen] = useState(false);
    const variantOfferBlurTimer = useRef(null);

    /** Копія техкарти: product_id для завантаження повного списку offers у селект. */
    const [copyOfferListProductId, setCopyOfferListProductId] = useState("");
    /** Підпис типу з offer джерела (властивості без кольору/розміру) — фільтр списку кольорів. */
    const [copyOfferTypeSig, setCopyOfferTypeSig] = useState("");
    /** Offer id зі зразка джерела — щоб після агрегації value селекта збігався з поточним рядком. */
    const [copyPreferredOfferId, setCopyPreferredOfferId] = useState("");
    const [copyProductVariantList, setCopyProductVariantList] = useState([]);
    const [copyProductVariantsLoading, setCopyProductVariantsLoading] = useState(false);

    const [lines, setLines] = useState([emptyLine()]);
    /** Поки true — показуємо підказку, що форма = копія джерела, треба обрати offer для нового варіанта. */
    const [techCardCopyAwaitingOfferUi, setTechCardCopyAwaitingOfferUi] = useState(false);
    /** '' — легасі без поля в БД; інакше complete | needs_work */
    const [readinessStatus, setReadinessStatus] = useState("");
    /** Додаткові product_id (ті самі лекала), через кому — синх із техкартою */
    const [sharedProductIdsText, setSharedProductIdsText] = useState("");
    /** Серіалізований стан після завантаження картки — для блокування «Зберегти» без змін. */
    const [editorSaveBaseline, setEditorSaveBaseline] = useState("");
    const [editorCardLoading, setEditorCardLoading] = useState(false);
    const cardsFilterBoot = useRef(true);

    const [editorModalOpen, setEditorModalOpen] = useState(false);
    /** true — відкрили через «+» (нова картка); false — рядок зі списку (редагування). */
    const [editorModalIsNewFlow, setEditorModalIsNewFlow] = useState(true);
    /** Режим «копія зі списку» — новий offer + рядки з іншої техкарти. */
    const [editorIsCopyFlow, setEditorIsCopyFlow] = useState(false);
    const [showChangeLog, setShowChangeLog] = useState(false);

    const focusVariantOffer = () => {
        if (variantOfferBlurTimer.current) {
            clearTimeout(variantOfferBlurTimer.current);
            variantOfferBlurTimer.current = null;
        }
        setVariantOfferOpen(true);
    };

    const blurVariantOffer = () => {
        if (variantOfferBlurTimer.current) {
            clearTimeout(variantOfferBlurTimer.current);
        }
        variantOfferBlurTimer.current = setTimeout(() => {
            setVariantOfferOpen(false);
            variantOfferBlurTimer.current = null;
        }, 200);
    };

    useEffect(
        () => () => {
            if (variantOfferBlurTimer.current) {
                clearTimeout(variantOfferBlurTimer.current);
            }
        },
        []
    );

    useEffect(() => {
        cardsRef.current = cards;
    }, [cards]);

    useEffect(() => {
        materialsRef.current = materials;
    }, [materials]);

    useEffect(() => {
        editorTargetRef.current = {
            productId: String(selectedProductId || ""),
            colorValue: String(colorValue || "").trim(),
        };
    }, [selectedProductId, colorValue]);

    /** Зелений банер без «хрестика» — зникає сам, щоб не злипався з іконкою закриття. */
    useEffect(() => {
        if (!okMsg) {
            return undefined;
        }
        const id = setTimeout(() => setOkMsg(null), 4200);
        return () => clearTimeout(id);
    }, [okMsg]);

    const loadMaterials = useCallback(async () => {
        const res = await axios.get("/api/materials");
        setMaterials(res.data || []);
    }, []);

    const loadCatalog = useCallback(async () => {
        const res = await axios.get(`${API_PROD}/catalog-products`);
        setCatalog(res.data || []);
    }, []);

    const loadCards = useCallback(async () => {
        const res = await axios.get(`${API_PROD}/tech-cards`, {
            params: filterQ.trim() ? { q: filterQ.trim() } : {},
        });
        setCards(res.data || []);
    }, [filterQ]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                await Promise.all([loadMaterials(), loadCatalog()]);
            } catch (e) {
                if (!cancelled) {
                    setError(
                        formatTechCardsAxiosError(e, "Помилка завантаження довідників")
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadMaterials, loadCatalog]);

    useEffect(() => {
        if (cardsFilterBoot.current) {
            cardsFilterBoot.current = false;
            loadCards().catch((e) => {
                setOkMsg(null);
                setError(formatTechCardsAxiosError(e, "Не вдалося оновити список техкарт"));
            });
            return;
        }
        const t = setTimeout(() => {
            loadCards().catch((e) => {
                setOkMsg(null);
                setError(formatTechCardsAxiosError(e, "Не вдалося оновити список техкарт"));
            });
        }, 280);
        return () => clearTimeout(t);
    }, [loadCards]);

    const techCardCoverageKeySet = useMemo(() => {
        const set = new Set();
        for (const c of cards) {
            const ck = String(
                c.colorKey || normalizeColorKeyClient(c.colorValue || "")
            ).trim();
            if (!ck) {
                continue;
            }
            const pid = Number(c.product_id);
            if (Number.isFinite(pid) && pid > 0) {
                set.add(`${pid}|${ck}`);
            }
            for (const sid of c.sharedForProductIds || []) {
                const n = Number(sid);
                if (Number.isFinite(n) && n > 0) {
                    set.add(`${n}|${ck}`);
                }
            }
        }
        return set;
    }, [cards]);

    const sortedTechCardsList = useMemo(() => {
        const key = listSort.key;
        if (!key) return cards;
        const dirAsc = listSort.dir === "asc";
        return [...cards].sort((a, b) => compareTechCardsList(a, b, key, dirAsc));
    }, [cards, listSort]);

    const toggleTechCardsListSort = useCallback((colKey) => {
        setListSort((prev) => {
            if (prev.key !== colKey) return { key: colKey, dir: "asc" };
            return { key: colKey, dir: prev.dir === "asc" ? "desc" : "asc" };
        });
    }, []);

    const variantSuggest = useMemo(
        () =>
            variantSuggestRaw.filter((v) => !variantOfferHasTechCard(v, techCardCoverageKeySet)),
        [variantSuggestRaw, techCardCoverageKeySet]
    );

    const copyVariantSelectOptions = useMemo(
        () =>
            (copyProductVariantList || []).filter(
                (v) => !variantOfferHasTechCard(v, techCardCoverageKeySet)
            ),
        [copyProductVariantList, techCardCoverageKeySet]
    );

    const copyOfferSelectValue = useMemo(() => {
        if (!editorIsCopyFlow || !copyVariantSelectOptions.length) {
            return "";
        }
        const pid = String(selectedProductId || "");
        const cvRaw = String(colorValue || "").trim();
        const ck = normalizeColorKeyClient(cvRaw);
        const sameColor = copyVariantSelectOptions.filter((v) => {
            if (String(v.product_id) !== pid) {
                return false;
            }
            const vck = v.colorKey || normalizeColorKeyClient(v.colorValue || "");
            if (vck !== ck) {
                return false;
            }
            if (copyOfferTypeSig) {
                return String(v.variantTypeSig || "") === String(copyOfferTypeSig);
            }
            return true;
        });
        if (sameColor.length === 0) {
            return "";
        }
        const sid = sampleProduct?.id;
        if (sid != null) {
            const exact = sameColor.find((v) => Number(v.offerId) === Number(sid));
            if (exact) {
                return String(exact.offerId);
            }
        }
        return String(sameColor[0].offerId);
    }, [
        editorIsCopyFlow,
        copyVariantSelectOptions,
        selectedProductId,
        colorValue,
        sampleProduct?.id,
        copyOfferTypeSig,
    ]);

    useEffect(() => {
        if (!editorModalOpen || !copyOfferListProductId || !editorIsCopyFlow) {
            setCopyProductVariantList([]);
            setCopyProductVariantsLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setCopyProductVariantsLoading(true);
            try {
                const params = { product_id: copyOfferListProductId };
                if (copyOfferTypeSig) {
                    params.type_sig = copyOfferTypeSig;
                }
                if (copyPreferredOfferId) {
                    params.preferred_offer_id = copyPreferredOfferId;
                }
                const res = await axios.get(`${API_PROD}/variants-by-product`, {
                    params,
                });
                if (!cancelled) {
                    setCopyProductVariantList(res.data || []);
                }
            } catch {
                if (!cancelled) {
                    setCopyProductVariantList([]);
                }
            } finally {
                if (!cancelled) {
                    setCopyProductVariantsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        editorModalOpen,
        editorIsCopyFlow,
        copyOfferListProductId,
        copyOfferTypeSig,
        copyPreferredOfferId,
    ]);

    useEffect(() => {
        if (!editorModalOpen) {
            setVariantSuggestRaw([]);
            return;
        }
        if (editorIsCopyFlow) {
            setVariantSuggestRaw([]);
            setVariantLoading(false);
            return;
        }
        const q = variantSearch.trim();
        if (q.length < 1) {
            setVariantSuggestRaw([]);
            return;
        }
        const t = setTimeout(async () => {
            setVariantLoading(true);
            try {
                const copyPid = techCardCopySourceProductIdRef.current;
                const params =
                    editorIsCopyFlow && copyPid
                        ? { q, product_id: copyPid }
                        : { q };
                const res = await axios.get(`${API_PROD}/variants`, { params });
                const { offers } = parseProductionVariantSearchResponse(res.data);
                setVariantSuggestRaw(offers);
            } catch {
                setVariantSuggestRaw([]);
            } finally {
                setVariantLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [variantSearch, editorModalOpen, editorIsCopyFlow]);

    const selectedCatalogEntry = catalog.find(
        (c) => String(c.product_id) === String(selectedProductId)
    );

    const isTechCardEditorDirty = useMemo(
        () =>
            serializeTechCardEditorState(lines, sharedProductIdsText, readinessStatus) !==
            editorSaveBaseline,
        [lines, sharedProductIdsText, readinessStatus, editorSaveBaseline]
    );

    const techCardEditorMaterialsTotalUAH = useMemo(() => {
        let sum = 0;
        let any = false;
        for (const L of lines) {
            const mat = materials.find((m) => String(m._id) === String(L.materialId));
            if (!mat) {
                continue;
            }
            const c = techCardEditorLineCostUAH(mat, L);
            if (c != null) {
                sum += c;
                any = true;
            }
        }
        return any ? roundMoneyDisplay2(sum) : null;
    }, [lines, materials]);

    const loadOneCard = useCallback(async () => {
        const pid = Number(selectedProductId);
        const cv = String(colorValue || "").trim();
        const requestPid = pid;
        const requestCv = cv;

        const stillCurrent = () => {
            const cur = editorTargetRef.current;
            return String(requestPid) === cur.productId && requestCv === cur.colorValue;
        };

        if (!pid || !cv) {
            setSampleProduct(null);
            setCardLoaded(null);
            const copyBoot = techCardCopySessionRef.current;
            if (copyBoot?.editorLines) {
                const bootLines = copyBoot.editorLines;
                setLines(bootLines);
                setSharedProductIdsText(copyBoot.sharedProductIdsText || "");
                setReadinessStatus(copyBoot.readinessStatus || "");
                setEditorSaveBaseline(
                    serializeTechCardEditorState(
                        bootLines,
                        copyBoot.sharedProductIdsText || "",
                        copyBoot.readinessStatus || ""
                    )
                );
                setEditorCardLoading(false);
                return;
            }
            techCardCopySessionRef.current = null;
            setEditorIsCopyFlow(false);
            setLines([emptyLine()]);
            setReadinessStatus("");
            setSharedProductIdsText("");
            setEditorSaveBaseline("");
            setEditorCardLoading(false);
            return;
        }
        setEditorCardLoading(true);
        setEditorSaveBaseline("");
        try {
            const res = await axios.get(`${API_PROD}/tech-cards/one`, {
                params: { product_id: pid, color_value: cv },
            });
            if (!stillCurrent()) {
                return;
            }
            const { card, sampleProduct: sp } = res.data;
            const copySessMid = techCardCopySessionRef.current;
            const sharedTxt = card?.sharedForProductIds?.length
                ? card.sharedForProductIds.join(", ")
                : copySessMid?.sharedProductIdsText ?? "";
            let readinessValue = "";
            if (card) {
                const rs = card.readinessStatus;
                readinessValue = rs === "complete" || rs === "needs_work" ? rs : "";
            } else if (
                copySessMid?.readinessStatus === "complete" ||
                copySessMid?.readinessStatus === "needs_work"
            ) {
                readinessValue = copySessMid.readinessStatus;
            } else {
                readinessValue = "needs_work";
            }
            const currentRowKey = `${pid}|${normalizeColorKeyClient(cv)}`;
            const treatCopySourceAsNew =
                editorIsCopyFlow &&
                techCardCopyAwaitingNewOfferRef.current &&
                Boolean(techCardCopySourceRowKeyRef.current) &&
                currentRowKey === techCardCopySourceRowKeyRef.current;

            setSampleProduct(sp || null);
            if (treatCopySourceAsNew) {
                setCardLoaded(null);
            } else {
                setCardLoaded(card ?? null);
            }
            setSharedProductIdsText(sharedTxt);
            setReadinessStatus(readinessValue);
            const fromCard =
                card?.lines?.map((L) => ({
                    key: `${L.materialId}-${Math.random()}`,
                    materialId: String(L.materialId),
                    normUnit: L.normUnit === "linear_m" ? "linear_m" : "stock",
                    qtyPerUnit: L.qtyPerUnit != null ? String(L.qtyPerUnit) : "",
                    note: L.note || "",
                })) || [];

            const mats = materialsRef.current || [];

            if (fromCard.length) {
                if (!treatCopySourceAsNew) {
                    techCardCopySessionRef.current = null;
                    setEditorIsCopyFlow(false);
                }
                setError(null);
                setLines(fromCard);
                setEditorSaveBaseline(
                    serializeTechCardEditorState(fromCard, sharedTxt, readinessValue)
                );
                return;
            }

            const copyApi = techCardCopySessionRef.current?.sourceApiLines;
            if (Array.isArray(copyApi) && copyApi.length && mats.length) {
                const sess = techCardCopySessionRef.current;
                techCardCopySessionRef.current = null;
                const newLines = mapCopySourceApiLinesToEditorLinesPreservingMaterials(copyApi);
                setLines(newLines);
                setMaterials((prev) =>
                    borrowKgGsmFromSourceLinesToMaterials(prev, copyApi, newLines)
                );
                const rsCopy =
                    sess?.readinessStatus === "complete" ||
                    sess?.readinessStatus === "needs_work"
                        ? sess.readinessStatus
                        : readinessValue;
                setSharedProductIdsText(sess?.sharedProductIdsText ?? sharedTxt);
                setReadinessStatus(rsCopy);
                setError(null);
                setOkMsg(
                    "Рядки з копії збережено; матеріали лишаються як у джерела — за потреби змініть окремі позиції під новий колір і збережіть."
                );
                /* Як при автопідстановці з sibling: baseline «не збігається», щоб «Зберегти» було доступне. */
                setEditorSaveBaseline(
                    serializeTechCardEditorState(
                        [emptyLine()],
                        sess?.sharedProductIdsText ?? sharedTxt,
                        rsCopy
                    )
                );
                return;
            }

            techCardCopySessionRef.current = null;
            setEditorIsCopyFlow(false);

            const targetKey = normalizeColorKeyClient(cv);
            const sibling = pickBestSiblingSourceCard(
                cardsRef.current,
                String(pid),
                targetKey
            );
            if (sibling?.lines?.length && mats.length) {
                const newLines = mapSiblingSourceLinesToEditorLines(
                    sibling.lines,
                    cv,
                    mats
                );
                setLines(newLines);
                setMaterials((prev) =>
                    borrowKgGsmFromSourceLinesToMaterials(prev, sibling.lines, newLines)
                );
                setError(null);
                setOkMsg(
                    "Норми витрат підставлено з іншого кольору цього товару — перевірте матеріали й збережіть."
                );
                // База — «як з сервера» (рядків не було); інакше diff зійде й «Зберегти» лишиться вимкненим
                setEditorSaveBaseline(
                    serializeTechCardEditorState([emptyLine()], sharedTxt, readinessValue)
                );
            } else {
                const empty = [emptyLine()];
                setLines(empty);
                setEditorSaveBaseline(
                    serializeTechCardEditorState(empty, sharedTxt, readinessValue)
                );
            }
        } catch (e) {
            if (!stillCurrent()) {
                return;
            }
            setOkMsg(null);
            setError(formatTechCardsAxiosError(e, "Не вдалося завантажити техкарту"));
            techCardCopySessionRef.current = null;
            techCardCopyAwaitingNewOfferRef.current = false;
            techCardCopySourceRowKeyRef.current = "";
            techCardCopySourceProductIdRef.current = "";
            setCopyOfferListProductId("");
            setCopyOfferTypeSig("");
            setCopyPreferredOfferId("");
            setCopyProductVariantList([]);
            setCopyProductVariantsLoading(false);
            setTechCardCopyAwaitingOfferUi(false);
            setEditorIsCopyFlow(false);
            setCardLoaded(null);
            setSampleProduct(null);
            setReadinessStatus("");
            setSharedProductIdsText("");
            const empty = [emptyLine()];
            setLines(empty);
            setEditorSaveBaseline(serializeTechCardEditorState(empty, "", ""));
        } finally {
            setEditorCardLoading(false);
        }
    }, [selectedProductId, colorValue, cards.length, materials.length, editorIsCopyFlow]);

    useEffect(() => {
        if (!editorModalOpen) {
            return;
        }
        const t = setTimeout(() => {
            loadOneCard();
        }, 250);
        return () => clearTimeout(t);
    }, [loadOneCard, editorModalOpen]);

    const clearEditor = () => {
        techCardCopySessionRef.current = null;
        techCardCopyAwaitingNewOfferRef.current = false;
        techCardCopySourceRowKeyRef.current = "";
        techCardCopySourceProductIdRef.current = "";
        setCopyOfferListProductId("");
        setCopyOfferTypeSig("");
        setCopyPreferredOfferId("");
        setCopyProductVariantList([]);
        setCopyProductVariantsLoading(false);
        setEditorIsCopyFlow(false);
        setTechCardCopyAwaitingOfferUi(false);
        setSelectedProductId("");
        setColorValue("");
        setSampleProduct(null);
        setCardLoaded(null);
        setLines([emptyLine()]);
        setVariantSearch("");
        setVariantSuggestRaw([]);
        setVariantOfferOpen(false);
        setReadinessStatus("");
        setSharedProductIdsText("");
        setEditorSaveBaseline("");
        setEditorCardLoading(false);
    };

    const closeEditorModal = () => {
        setEditorModalOpen(false);
        clearEditor();
    };

    const openNewEditorModal = () => {
        setError(null);
        setOkMsg(null);
        clearEditor();
        setEditorModalIsNewFlow(true);
        setEditorModalOpen(true);
    };

    const openCopyTechCardFromList = useCallback(async (c, e) => {
        e?.stopPropagation?.();
        setError(null);
        setOkMsg(null);
        const pid = Number(c.product_id);
        const cv = String(c.colorValue || c.colorKey || "").trim();
        if (!Number.isFinite(pid) || pid <= 0 || !cv) {
            setError("Немає товару або кольору для копіювання.");
            return;
        }
        let sourceVariantTypeSig = "";
        let sampleProductIdForCopy = "";
        try {
            const res = await axios.get(`${API_PROD}/tech-cards/one`, {
                params: { product_id: pid, color_value: cv },
            });
            sourceVariantTypeSig = String(res.data.sourceVariantTypeSig || "");
            sampleProductIdForCopy =
                res.data.sampleProduct?.id != null ? String(res.data.sampleProduct.id) : "";
            const card = res.data.card;
            const sharedTxt =
                card?.sharedForProductIds?.length
                    ? card.sharedForProductIds.join(", ")
                    : "";
            let readinessValue = "needs_work";
            if (card) {
                const rs = card.readinessStatus;
                readinessValue =
                    rs === "complete" || rs === "needs_work" ? rs : "needs_work";
            }
            const apiLines = Array.isArray(card?.lines) ? card.lines : [];
            const fromCard = apiLines.map((L) => ({
                key: `${L.materialId}-${Math.random()}`,
                materialId: String(L.materialId),
                normUnit: L.normUnit === "linear_m" ? "linear_m" : "stock",
                qtyPerUnit: L.qtyPerUnit != null ? String(L.qtyPerUnit) : "",
                note: L.note || "",
            }));
            const editorLines = fromCard.length ? fromCard : [emptyLine()];
            techCardCopySessionRef.current = {
                editorLines,
                sourceApiLines: apiLines,
                sharedProductIdsText: sharedTxt,
                readinessStatus: readinessValue,
            };
            setLines(editorLines);
            setSharedProductIdsText(sharedTxt);
            setReadinessStatus(readinessValue);
            setEditorSaveBaseline(
                serializeTechCardEditorState(editorLines, sharedTxt, readinessValue)
            );
        } catch (err) {
            setError(
                formatTechCardsAxiosError(err, "Не вдалося завантажити техкарту для копіювання")
            );
            return;
        }
        const srcPid = String(c.product_id);
        const srcCv = String(c.colorValue || c.colorKey || "").trim();
        techCardCopyAwaitingNewOfferRef.current = true;
        techCardCopySourceRowKeyRef.current = `${Number(c.product_id)}|${normalizeColorKeyClient(srcCv)}`;
        techCardCopySourceProductIdRef.current = String(Number(c.product_id) || "");
        setCopyOfferListProductId(String(Number(c.product_id) || ""));
        setCopyOfferTypeSig(sourceVariantTypeSig);
        setCopyPreferredOfferId(sampleProductIdForCopy);
        setTechCardCopyAwaitingOfferUi(true);
        setEditorModalIsNewFlow(true);
        setEditorIsCopyFlow(true);
        setSelectedProductId(srcPid);
        setColorValue(srcCv);
        setSampleProduct(null);
        setCardLoaded(null);
        setVariantSearch("");
        setVariantSuggestRaw([]);
        setVariantOfferOpen(false);
        setEditorCardLoading(false);
        setEditorModalOpen(true);
    }, []);

    const openEditorForCard = (c) => {
        setError(null);
        setOkMsg(null);
        techCardCopySessionRef.current = null;
        techCardCopyAwaitingNewOfferRef.current = false;
        techCardCopySourceRowKeyRef.current = "";
        techCardCopySourceProductIdRef.current = "";
        setCopyOfferListProductId("");
        setCopyOfferTypeSig("");
        setCopyPreferredOfferId("");
        setCopyProductVariantList([]);
        setCopyProductVariantsLoading(false);
        setEditorIsCopyFlow(false);
        setTechCardCopyAwaitingOfferUi(false);
        setEditorModalIsNewFlow(false);
        setSelectedProductId(String(c.product_id));
        setColorValue(c.colorValue || c.colorKey || "");
        setEditorModalOpen(true);
    };

    const applyVariantShortcut = (v) => {
        setError(null);
        setOkMsg(null);
        techCardCopyAwaitingNewOfferRef.current = false;
        setTechCardCopyAwaitingOfferUi(false);
        if (!v.colorValue) {
            setOkMsg(null);
            setError("У цього offer немає властивості «Колір» у CRM — додайте колір у KeyCRM або оберіть товар вручну.");
            return;
        }
        setSelectedProductId(String(v.product_id));
        setColorValue(v.colorValue);
        setVariantSearch("");
    };

    const parseDecimalInput = (s) => {
        if (s === undefined || s === null || String(s).trim() === "") {
            return NaN;
        }
        return Number(String(s).trim().replace(",", "."));
    };

    const saveCard = async (e) => {
        e.preventDefault();
        const pid = Number(selectedProductId);
        const cv = String(colorValue || "").trim();
        if (!pid || !cv) return;
        setSaving(true);
        setError(null);
        setOkMsg(null);
        let savePayloadSummary = null;
        try {
            const payloadLines = lines
                .filter((L) => {
                    const mid =
                        L.materialId &&
                        typeof L.materialId === "object" &&
                        L.materialId._id != null
                            ? L.materialId._id
                            : L.materialId;
                    if (!mid || String(L.qtyPerUnit).trim() === "") {
                        return false;
                    }
                    const q = parseDecimalInput(L.qtyPerUnit);
                    return Number.isFinite(q) && q >= 0;
                })
                .map((L) => {
                    const mid =
                        L.materialId &&
                        typeof L.materialId === "object" &&
                        L.materialId._id != null
                            ? String(L.materialId._id)
                            : String(L.materialId || "");
                    const mat = materials.find((m) => String(m._id) === mid);
                    const isLinear = L.normUnit === "linear_m";
                    const row = {
                        materialId: mid,
                        normUnit: isLinear ? "linear_m" : "stock",
                        qtyPerUnit: parseDecimalInput(L.qtyPerUnit),
                        note: L.note || "",
                    };
                    if (isLinear && mat) {
                        const eff = effectiveKgPm(mat);
                        const k =
                            eff != null && Number.isFinite(Number(eff)) && Number(eff) > 0
                                ? Number(eff)
                                : NaN;
                        if (Number.isFinite(k) && k > 0) {
                            row.kgPerLinearMeter = k;
                        }
                        const rawG = mat.fabricGsm;
                        if (
                            rawG !== undefined &&
                            rawG !== null &&
                            String(rawG).trim() !== ""
                        ) {
                            const g = parseDecimalInput(rawG);
                            if (Number.isFinite(g) && g > 0) {
                                row.fabricGsm = g;
                            }
                        }
                    }
                    return row;
                });
            const hadMaterialRows = lines.some((L) => {
                const mid =
                    L.materialId &&
                    typeof L.materialId === "object" &&
                    L.materialId._id != null
                        ? L.materialId._id
                        : L.materialId;
                return Boolean(mid) && String(L.qtyPerUnit || "").trim() !== "";
            });
            if (hadMaterialRows && payloadLines.length === 0) {
                setOkMsg(null);
                setError(
                    "Некоректна кількість у рядку матеріалу. Для дробів використовуйте кому або крапку (наприклад 0,9 або 0.9)."
                );
                setSaving(false);
                return;
            }
            savePayloadSummary = {
                product_id: pid,
                color_value: cv,
                lines: payloadLines.map((r) => ({
                    materialId: r.materialId,
                    normUnit: r.normUnit,
                    qtyPerUnit: r.qtyPerUnit,
                    kgPerLinearMeter: r.kgPerLinearMeter,
                    fabricGsm: r.fabricGsm,
                })),
            };
            const sharedIds = sharedProductIdsText
                .split(/[,;\s]+/)
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s) => Number(s))
                .filter((n) => Number.isFinite(n) && n > 0 && n !== pid);
            await axios.put(`${API_PROD}/tech-cards/one`, {
                product_id: pid,
                color_value: cv,
                lines: payloadLines,
                sharedForProductIds: sharedIds,
                readinessStatus,
            });
            setError(null);
            try {
                await loadCards();
            } catch (loadErr) {
                setOkMsg(null);
                setError(
                    `${formatTechCardsAxiosError(loadErr, "Не вдалося оновити список")} Збереження на сервері виконано — оновіть сторінку або спробуйте ще раз.`
                );
                return;
            }
            setOkMsg("Техкарту збережено");
            closeEditorModal();
        } catch (e) {
            setOkMsg(null);
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.warn("[TechCards save]", {
                    status: e.response?.status,
                    code: e.response?.data?.code,
                    serverError: e.response?.data?.error,
                    debug: e.response?.data?.debug,
                    payloadSummary: savePayloadSummary,
                });
            }
            setError(formatTechCardsAxiosError(e, "Не вдалося зберегти техкарту"));
        } finally {
            setSaving(false);
        }
    };

    const deleteTechCardPair = useCallback(async (pidRaw, colorValRaw) => {
        const pid = Number(pidRaw);
        const cv = String(colorValRaw || "").trim();
        if (!pid || Number.isNaN(pid) || !cv) return false;
        setSaving(true);
        setError(null);
        try {
            await axios.delete(`${API_PROD}/tech-cards/one`, {
                params: { product_id: pid, color_value: cv },
            });
            try {
                await loadCards();
            } catch (loadErr) {
                setOkMsg(null);
                setError(
                    `${formatTechCardsAxiosError(loadErr, "Не вдалося оновити список")} Видалення на сервері виконано — оновіть сторінку.`
                );
                return true;
            }
            setOkMsg("Видалено");
            return true;
        } catch (e) {
            setOkMsg(null);
            setError(formatTechCardsAxiosError(e, "Не вдалося видалити техкарту"));
            return false;
        } finally {
            setSaving(false);
        }
    }, [loadCards]);

    const confirmClearEditorForm = () => {
        if (
            !window.confirm(
                "Точно очистити форму? Буде скинуто вибраний товар, колір і всі рядки матеріалів у цьому вікні."
            )
        ) {
            return;
        }
        clearEditor();
    };

    const deleteCard = async () => {
        const pid = Number(selectedProductId);
        const cv = String(colorValue || "").trim();
        if (!pid || !cv) return;
        if (
            !window.confirm(
                "Точно видалити збережену техкарту для цього товару та кольору з бази? Цю дію не можна скасувати."
            )
        ) {
            return;
        }
        const ok = await deleteTechCardPair(pid, cv);
        if (ok) {
            closeEditorModal();
        }
    };

    const isSameCardSelection = (c) =>
        String(selectedProductId) === String(c.product_id) &&
        normalizeColorKeyClient(colorValue) ===
            normalizeColorKeyClient(c.colorValue || c.colorKey || "");

    const deleteCardFromList = async (c, e) => {
        e.stopPropagation();
        const name = String(c.name || "Товар").trim();
        const col = String(c.colorValue || c.colorKey || "—").trim();
        if (
            !window.confirm(
                `Точно видалити техкарту?\n${name}\nКолір: ${col}`
            )
        ) {
            return;
        }
        const ok = await deleteTechCardPair(c.product_id, c.colorValue || c.colorKey);
        if (ok) {
            if (isSameCardSelection(c)) {
                closeEditorModal();
            }
        }
    };

    const displayImage =
        sampleProduct?.thumbnailUrl || cardLoaded?.thumbnailUrl || cardLoaded?.imageOverrideUrl || "";

    const editorActive = Boolean(selectedProductId && String(colorValue || "").trim());
    const rowKey = (c) => `${c.product_id}-${c.colorKey}`;

    /** Offer-пошук лише для нової картки; для вже збереженої або відкриття зі списку не показуємо. */
    const showVariantOfferBlock = editorModalIsNewFlow && !cardLoaded?._id;

    const techCardTitleName =
        selectedCatalogEntry?.name ||
        cardLoaded?.name ||
        (selectedProductId ? `Товар id ${selectedProductId}` : "");

    const renderListSortTh = (sortKey, label, opts = {}) => {
        const { className = "", style } = opts;
        const active = listSort.key === sortKey;
        return (
            <th
                scope="col"
                className={`text-nowrap ${className}`.trim() || undefined}
                style={style}
                aria-sort={
                    active ? (listSort.dir === "asc" ? "ascending" : "descending") : undefined
                }
            >
                <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none p-0 text-body tech-cards-list-sort-btn"
                    onClick={() => toggleTechCardsListSort(sortKey)}
                    aria-label={`Сортувати за колонкою «${label}»`}
                >
                    <span className="fw-semibold">{label}</span>
                    {active ? (
                        listSort.dir === "asc" ? (
                            <ArrowUp className="ms-1 opacity-75" size={14} aria-hidden />
                        ) : (
                            <ArrowDown className="ms-1 opacity-75" size={14} aria-hidden />
                        )
                    ) : null}
                </button>
            </th>
        );
    };

    if (loading && !catalog.length) {
        return <p className="text-muted">Завантаження…</p>;
    }

    return (
        <div className="production-tab-panel tab-content">
            <h5 className="mb-3">Техкарти</h5>
            <p className="text-muted small mb-3">
                Одна техкарта на <strong>товар + колір</strong> (product з CRM + колір). Розмір не
                враховується. У модалці редактору товар і колір обираються через{" "}
                <strong>пошук offer</strong> або вже підставлені при відкритті рядка зі списку. Норми: у{" "}
                <strong>Од. вим</strong> — одиниці складу (шт, кг…) або <strong>м.п. → кг</strong> (лише якщо
                матеріал на складі в <strong>кг</strong>; <strong>вага 1 м.п.</strong> — у властивостях
                матеріалу (ключ «Вага 1 м.п.»).
                Для нового варіанта скористайтеся кнопкою <strong>копіювання</strong> в рядку списку — відкриється
                форма з тими ж нормами; далі оберіть offer нового товару/кольору.
            </p>

            <div className="tech-cards-page-alerts mb-2">
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        <span className="tech-cards-alert-body">{error}</span>
                    </Alert>
                )}
                {okMsg && (
                    <Alert variant="success" className="tech-cards-alert-success py-2 mb-0">
                        {okMsg}
                    </Alert>
                )}
            </div>

            <section className="mb-0" aria-label="Список техкарт">
                        <Form.Label htmlFor="tech-cards-filter-q" className="visually-hidden">
                            Пошук техкарт
                        </Form.Label>
                        <div
                            className="d-flex flex-nowrap align-items-stretch gap-2 mb-2"
                            style={{ minWidth: 0 }}
                        >
                            <Form.Control
                                id="tech-cards-filter-q"
                                className="flex-grow-1"
                                style={{ minWidth: 0 }}
                                value={filterQ}
                                onChange={(e) => setFilterQ(e.target.value)}
                                placeholder="Назва товару або колір…"
                                aria-label="Пошук збережених техкарт"
                            />
                            <Button
                                type="button"
                                variant="outline-secondary"
                                className="px-2 flex-shrink-0 align-self-stretch d-flex align-items-center"
                                onClick={() => setShowChangeLog(true)}
                                title="Історія змін техкарт"
                                aria-label="Історія змін техкарт"
                            >
                                <History size={18} aria-hidden />
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                className="px-3 flex-shrink-0 align-self-stretch"
                                onClick={openNewEditorModal}
                                title="Створити нову техкарту"
                                aria-label="Створити нову техкарту"
                            >
                                +
                            </Button>
                        </div>

                        <div
                            className="materials-hub-table-scroll border rounded"
                            style={{ maxHeight: "min(78vh, 720px)" }}
                        >
                            <Table
                                bordered
                                hover
                                size="sm"
                                className="materials-hub-table align-middle mb-0 tech-cards-list-table"
                            >
                                <thead className="table-light">
                                    <tr>
                                        <th
                                            className="text-nowrap tech-cards-list-col-num"
                                            scope="col"
                                        >
                                            №
                                        </th>
                                        {renderListSortTh("name", "Товар")}
                                        {renderListSortTh("color", "Колір")}
                                        {renderListSortTh("materials", "Матеріали", {
                                            style: { minWidth: 200, maxWidth: 360 },
                                        })}
                                        {renderListSortTh("cost", "Витрата", {
                                            style: { width: 120 },
                                        })}
                                        {renderListSortTh("status", "Статус", {
                                            style: { width: 118 },
                                        })}
                                        <th
                                            className="text-nowrap tech-cards-list-col-actions"
                                            scope="col"
                                            style={{ width: 124 }}
                                        >
                                            Дії
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTechCardsList.map((c, listIdx) => (
                                        <tr
                                            key={rowKey(c)}
                                            style={{
                                                cursor: "pointer",
                                                background:
                                                    String(selectedProductId) ===
                                                        String(c.product_id) &&
                                                    String(colorValue || "").trim().toLowerCase() ===
                                                        String(c.colorValue || "").trim().toLowerCase()
                                                        ? "rgba(13,110,253,0.08)"
                                                        : undefined,
                                            }}
                                            onClick={() => openEditorForCard(c)}
                                        >
                                            <td className="small text-muted tech-cards-list-col-num">
                                                {listIdx + 1}
                                            </td>
                                            <td className="small">
                                                <div>{c.name}</div>
                                            </td>
                                            <td className="small text-muted">
                                                {c.colorValue || "—"}
                                            </td>
                                            <td
                                                className="small text-muted"
                                                style={{
                                                    maxWidth: 360,
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-word",
                                                }}
                                                title={c.materialsSummary || ""}
                                            >
                                                {c.materialsSummary || "—"}
                                            </td>
                                            <td className="small text-nowrap">
                                                {c.materialsCostUAH != null &&
                                                Number.isFinite(Number(c.materialsCostUAH))
                                                    ? `${Number(c.materialsCostUAH).toFixed(2)} ₴`
                                                    : "—"}
                                            </td>
                                            <td className="small align-middle tech-card-readiness-cell">
                                                {techCardReadinessBadge(c)}
                                            </td>
                                            <td
                                                className="align-middle py-0 ps-1 tech-cards-list-col-actions"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    size="sm"
                                                    className="p-1 text-decoration-none border-0 shadow-none"
                                                    style={{ color: "#212529" }}
                                                    disabled={saving}
                                                    title="Редагувати"
                                                    aria-label="Редагувати техкарту"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditorForCard(c);
                                                    }}
                                                >
                                                    <Pencil size={18} aria-hidden />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    size="sm"
                                                    className="p-1 text-decoration-none border-0 shadow-none"
                                                    style={{ color: "#212529" }}
                                                    disabled={saving}
                                                    title="Копіювати як нову техкарту (інший offer)"
                                                    aria-label="Копіювати техкарту"
                                                    onClick={(e) => void openCopyTechCardFromList(c, e)}
                                                >
                                                    <Copy size={18} aria-hidden />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    size="sm"
                                                    className="p-1 text-danger"
                                                    disabled={saving}
                                                    title="Видалити"
                                                    aria-label="Видалити техкарту"
                                                    onClick={(e) => deleteCardFromList(c, e)}
                                                >
                                                    <Trash2 size={18} aria-hidden />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {!cards.length && (
                                <p className="text-muted small px-3 py-2 mb-0">Нічого не знайдено.</p>
                            )}
                        </div>
                    </section>

            <Modal
                show={editorModalOpen}
                onHide={() => {
                    if (!saving) closeEditorModal();
                }}
                size="xl"
                scrollable
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editorIsCopyFlow
                            ? "Копія техкарти"
                            : editorModalIsNewFlow
                              ? "Нова техкарта"
                              : "Редактор техкарти"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "min(85vh, 900px)", overflowY: "auto" }}>
                    {showVariantOfferBlock ? (
                        <div className="mb-4 p-3 border border-primary border-opacity-25 rounded bg-light">
                            <Form.Label className="fw-semibold small d-block mb-2">
                                {editorIsCopyFlow ? "Інший колір (той самий тип)" : "Швидкий вибір з offer"}
                            </Form.Label>
                            {editorIsCopyFlow ? (
                                <>
                                    <p className="text-muted small mb-2 mb-md-3">
                                        Нижче — техкарта з джерела (товар і колір уже підставлені). Оберіть{" "}
                                        <strong>інший колір</strong> у селекті (без збереженої техкарти для пари
                                        товар+колір) — зʼявиться нова назва; норми й матеріали лишаються з копії.
                                        {copyOfferTypeSig ? (
                                            <>
                                                {" "}
                                                У списку лише offers <strong>того ж типу</strong>, що й у
                                                скопійованого варіанта (усі інші типи цього товару приховані). Розміри
                                                зведені в один рядок на колір.
                                            </>
                                        ) : (
                                            <>
                                                {" "}
                                                Розміри зведені в один рядок на колір.
                                            </>
                                        )}
                                    </p>
                                    <Form.Label
                                        htmlFor="tech-card-copy-offer-select"
                                        className="small fw-semibold mb-1 d-block"
                                    >
                                        Колір
                                    </Form.Label>
                                    <FormControl
                                        size="small"
                                        fullWidth
                                        sx={TECH_CARD_MUI_FIELD_SX}
                                        disabled={copyProductVariantsLoading}
                                    >
                                        <Select
                                            id="tech-card-copy-offer-select"
                                            value={copyOfferSelectValue}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                const v = copyVariantSelectOptions.find(
                                                    (x) => String(x.offerId) === String(id)
                                                );
                                                if (v) {
                                                    applyVariantShortcut(v);
                                                }
                                            }}
                                            displayEmpty
                                            input={<OutlinedInput notched={false} />}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: { maxHeight: 320 },
                                                },
                                            }}
                                            inputProps={{
                                                "aria-label": "Колір для нового варіанта (копія техкарти)",
                                            }}
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return (
                                                        <span className="text-muted">
                                                            — оберіть колір —
                                                        </span>
                                                    );
                                                }
                                                const v = copyVariantSelectOptions.find(
                                                    (x) => String(x.offerId) === String(selected)
                                                );
                                                if (!v) {
                                                    return (
                                                        <span className="text-muted">
                                                            — оберіть колір —
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    v.variantLabelNoSize ||
                                                    v.colorValue ||
                                                    v.display ||
                                                    "—"
                                                );
                                            }}
                                        >
                                            <MenuItem value="">
                                                <span className="text-muted">— оберіть колір —</span>
                                            </MenuItem>
                                            {copyVariantSelectOptions.map((v) => {
                                                const label =
                                                    v.variantLabelNoSize ||
                                                    v.colorValue ||
                                                    "—";
                                                return (
                                                    <MenuItem key={v.offerId} value={String(v.offerId)}>
                                                        <span className="small">{label}</span>
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                    {copyProductVariantsLoading ? (
                                        <small className="text-muted d-block mt-2">Завантаження списку…</small>
                                    ) : null}
                                    {!copyProductVariantsLoading &&
                                    copyProductVariantList.length > 0 &&
                                    copyVariantSelectOptions.length === 0 ? (
                                        <small className="text-muted d-block mt-2">
                                            Усі offers цього товару вже мають збережену техкарту для відповідного
                                            кольору.
                                        </small>
                                    ) : null}
                                    {!copyProductVariantsLoading && copyProductVariantList.length === 0 ? (
                                        <small className="text-muted d-block mt-2">
                                            Не вдалося завантажити offers або їх немає у CRM для цього товару.
                                        </small>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <Form.Control
                                        value={variantSearch}
                                        onChange={(e) => setVariantSearch(e.target.value)}
                                        onFocus={focusVariantOffer}
                                        onBlur={blurVariantOffer}
                                        placeholder="Пошук offer: кілька слів — усі мають збігатися (назва, SKU, властивості)"
                                        autoComplete="off"
                                    />
                                    {variantOfferOpen && variantLoading && (
                                        <small className="text-muted d-block mt-2">Пошук…</small>
                                    )}
                                    {variantOfferOpen &&
                                        !variantLoading &&
                                        variantSuggestRaw.length > 0 &&
                                        variantSuggest.length === 0 && (
                                            <small className="text-muted d-block mt-2">
                                                Усі знайдені варіанти вже мають техкарту для цього товару та кольору.
                                            </small>
                                        )}
                                    {variantOfferOpen && variantSuggest.length > 0 && (
                                        <div
                                            className="list-group mt-2 shadow-sm"
                                            style={{ maxHeight: 220, overflowY: "auto" }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            role="listbox"
                                            aria-label="Підказки offer"
                                        >
                                            {variantSuggest.map((s) => (
                                                <button
                                                    key={s.offerId}
                                                    type="button"
                                                    className="list-group-item list-group-item-action py-2 small text-start"
                                                    onClick={() => applyVariantShortcut(s)}
                                                >
                                                    {s.display}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : null}

                    {!editorActive ? (
                        <p className="text-muted small mb-0">
                            {showVariantOfferBlock ? (
                                <>
                                    {editorIsCopyFlow ? (
                                        <>
                                            Оберіть <strong>колір</strong> у селекті вище — зʼявиться{" "}
                                            <strong>назва техкарти</strong> та форма норм матеріалів. Кольори, для яких
                                            техкарта вже збережена, не показуються.
                                        </>
                                    ) : (
                                        <>
                                            Оберіть offer у блоці вище — зʼявиться{" "}
                                            <strong>назва техкарти</strong> (товар і колір) та форма норм матеріалів.
                                            Варіанти, для яких техкарта вже збережена, у підказках не показуються.
                                        </>
                                    )}
                                </>
                            ) : (
                                <>Завантаження або оберіть техкарту зі списку на сторінці.</>
                            )}
                        </p>
                    ) : (
                        <form id="tech-card-editor-form" onSubmit={saveCard}>
                            <div className="mb-3 p-3 border rounded bg-white">
                                <div className="d-flex align-items-stretch gap-2">
                                    <div className="d-flex flex-wrap gap-3 align-items-start flex-grow-1 min-w-0">
                                        <div className="flex-shrink-0">
                                            {displayImage ? (
                                                <img
                                                    src={displayImage}
                                                    alt=""
                                                    className="img-thumbnail"
                                                    style={{
                                                        maxHeight: 140,
                                                        maxWidth: 140,
                                                        width: "auto",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="img-thumbnail d-flex align-items-center justify-content-center text-muted small text-center px-2"
                                                    style={{
                                                        width: 100,
                                                        minHeight: 100,
                                                        maxWidth: 140,
                                                    }}
                                                >
                                                    Немає фото
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow-1 min-w-0 d-flex flex-column">
                                            <div className="small text-muted text-uppercase mb-1 lh-sm">
                                                Назва техкарти
                                            </div>
                                            <div className="fs-5 fw-semibold mb-1">
                                                {techCardTitleName}
                                                <span className="text-muted fw-normal">
                                                    {" "}
                                                    — {colorValue}
                                                </span>
                                            </div>
                                            {cardLoaded?._id ? (
                                                <div className="small text-success mb-0">
                                                    Збережена техкарта
                                                </div>
                                            ) : (
                                                <div className="small text-warning mb-0">
                                                    Нова техкарта (ще не збережена)
                                                </div>
                                            )}
                                            {techCardCopyAwaitingOfferUi ? (
                                                <div className="small text-muted mb-0 mt-1">
                                                    Це копія вихідної техкарти. Щоб зберегти як нову — оберіть offer
                                                    іншого варіанта в блоці вище.
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div
                                        className="d-flex flex-column align-items-end justify-content-between flex-shrink-0 gap-2 ps-1"
                                        style={{ minWidth: 220, maxWidth: 300 }}
                                    >
                                        <div className="d-flex gap-1 flex-shrink-0">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                size="sm"
                                                className="d-inline-flex align-items-center justify-content-center px-2"
                                                disabled={saving}
                                                title="Очистити форму"
                                                aria-label="Очистити форму"
                                                onClick={confirmClearEditorForm}
                                            >
                                                <Eraser size={18} aria-hidden />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline-danger"
                                                size="sm"
                                                className="d-inline-flex align-items-center justify-content-center px-2"
                                                disabled={saving || !cardLoaded?._id}
                                                title="Видалити техкарту"
                                                aria-label="Видалити техкарту"
                                                onClick={deleteCard}
                                            >
                                                <Trash2 size={18} aria-hidden />
                                            </Button>
                                        </div>
                                        <div className="w-100 flex-shrink-0">
                                            <Form.Label
                                                htmlFor="tech-card-readiness-status"
                                                className="small fw-semibold mb-1 d-block text-end"
                                            >
                                                Статус техкарти
                                            </Form.Label>
                                            <FormControl
                                                size="small"
                                                fullWidth
                                                sx={TECH_CARD_MUI_FIELD_SX}
                                            >
                                                <Select
                                                    id="tech-card-readiness-status"
                                                    value={
                                                        readinessStatus === "complete" ||
                                                        readinessStatus === "needs_work"
                                                            ? readinessStatus
                                                            : ""
                                                    }
                                                    onChange={(e) =>
                                                        setReadinessStatus(e.target.value)
                                                    }
                                                    displayEmpty
                                                    input={<OutlinedInput notched={false} />}
                                                    inputProps={{
                                                        "aria-label": "Статус готовності техкарти",
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <span className="text-muted">Не вказано</span>
                                                    </MenuItem>
                                                    <MenuItem value="needs_work">
                                                        Потрібно ще доповнити
                                                    </MenuItem>
                                                    <MenuItem value="complete">
                                                        Повністю готова
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive border rounded mb-2">
                                <Table
                                    bordered
                                    hover
                                    size="sm"
                                    className="align-middle mb-0 tech-card-materials-editor-table"
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th
                                                className="text-center text-nowrap"
                                                style={{ width: 40 }}
                                            >
                                                #
                                            </th>
                                            <th className="text-start">Матеріал</th>
                                            <th
                                                className="text-nowrap tech-card-th-unit-price text-end"
                                                title="Ціна за одиницю обліку (з картки матеріалу, ₴)"
                                            >
                                                Ціна за од.
                                            </th>
                                            <th className="text-nowrap tech-card-th-qty">Кількість</th>
                                            <th className="text-nowrap tech-card-th-unit">Од. вим</th>
                                            <th
                                                className="text-nowrap tech-card-th-line-cost text-end"
                                                title="Вартість матеріалу на 1 готовий виріб за поточною нормою"
                                            >
                                                На 1 виріб
                                            </th>
                                            <th className="text-nowrap tech-card-th-actions">Дії</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((L, rowIndex) => {
                                            const matRow = materials.find(
                                                (m) => String(m._id) === String(L.materialId)
                                            );
                                            const materialAutocompleteOptions =
                                                materialOptionsForProductEditor(
                                                    materials,
                                                    selectedProductId,
                                                    matRow
                                                );
                                            const unitLbl = matRow?.unit || "…";
                                            const isLinearM = L.normUnit === "linear_m";
                                            const linearWarn =
                                                isLinearM &&
                                                L.materialId &&
                                                matRow &&
                                                !materialNeedsKgPerLinearMeter(matRow);
                                            return (
                                                <React.Fragment key={L.key}>
                                                    <tr>
                                                        <td className="text-center text-muted small py-1">
                                                            {rowIndex + 1}
                                                        </td>
                                                        <td className="py-1 text-start">
                                                            <Autocomplete
                                                                size="small"
                                                                sx={TECH_CARD_MUI_FIELD_SX}
                                                                componentsProps={TECH_CARD_AUTOCOMPLETE_COMPONENTS_PROPS}
                                                                ListboxProps={{
                                                                    sx: TECH_CARD_AUTOCOMPLETE_LISTBOX_SX,
                                                                }}
                                                                options={materialAutocompleteOptions}
                                                                getOptionLabel={(m) =>
                                                                    materialAutocompleteHeadline(m)
                                                                }
                                                                renderOption={(props, option) => {
                                                                    const sup = String(
                                                                        option.lastSupplierName || ""
                                                                    ).trim();
                                                                    return (
                                                                        <li {...props}>
                                                                            <div className="d-flex flex-column lh-sm">
                                                                                <span>
                                                                                    {materialAutocompleteHeadline(
                                                                                        option
                                                                                    )}
                                                                                </span>
                                                                                {sup ? (
                                                                                    <span
                                                                                        className="text-muted pt-1"
                                                                                        style={{
                                                                                            fontSize: "0.75rem",
                                                                                            lineHeight: 1.2,
                                                                                        }}
                                                                                    >
                                                                                        {sup}
                                                                                    </span>
                                                                                ) : null}
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                }}
                                                                value={matRow || null}
                                                                onChange={(_, newVal) =>
                                                                    setLines((prev) =>
                                                                        prev.map((x) => {
                                                                            if (x.key !== L.key) {
                                                                                return x;
                                                                            }
                                                                            if (!newVal) {
                                                                                return { ...x, materialId: "" };
                                                                            }
                                                                            const stockNorm =
                                                                                (x.normUnit || "stock") === "stock";
                                                                            const isPcs =
                                                                                normalizeMaterialUnitToCode(
                                                                                    newVal.unit
                                                                                ) === "шт";
                                                                            return {
                                                                                ...x,
                                                                                materialId: String(newVal._id),
                                                                                qtyPerUnit:
                                                                                    stockNorm && isPcs
                                                                                        ? "1"
                                                                                        : x.qtyPerUnit,
                                                                            };
                                                                        })
                                                                    )
                                                                }
                                                                isOptionEqualToValue={(a, b) =>
                                                                    String(a?._id) === String(b?._id)
                                                                }
                                                                filterOptions={(opts, state) => {
                                                                    const q = state.inputValue
                                                                        .trim()
                                                                        .toLowerCase();
                                                                    if (!q) {
                                                                        return opts.slice(0, 200);
                                                                    }
                                                                    return opts
                                                                        .filter((m) => {
                                                                            const typeName = String(
                                                                                m.typeId?.name || ""
                                                                            ).toLowerCase();
                                                                            const color = String(
                                                                                m.color || ""
                                                                            ).toLowerCase();
                                                                            const unit = String(
                                                                                m.unit || ""
                                                                            ).toLowerCase();
                                                                            const pairsHay =
                                                                                materialPropertyPairsSearchBlob(
                                                                                    m.propertyPairs
                                                                                );
                                                                            const supHay = String(
                                                                                m.lastSupplierName || ""
                                                                            ).toLowerCase();
                                                                            return (
                                                                                typeName.includes(q) ||
                                                                                color.includes(q) ||
                                                                                unit.includes(q) ||
                                                                                pairsHay.includes(q) ||
                                                                                supHay.includes(q)
                                                                            );
                                                                        })
                                                                        .slice(0, 200);
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        hiddenLabel
                                                                        placeholder="Тип, колір…"
                                                                        size="small"
                                                                        InputProps={{
                                                                            ...params.InputProps,
                                                                            notched: false,
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </td>
                                                        <td className="py-1 text-end text-nowrap small tech-card-td-unit-price align-middle">
                                                            {matRow ? (
                                                                (() => {
                                                                    const pu =
                                                                        effectiveStockUnitPriceUAH(
                                                                            matRow
                                                                        );
                                                                    if (
                                                                        pu != null &&
                                                                        Number.isFinite(pu) &&
                                                                        pu >= 0
                                                                    ) {
                                                                        const u = String(
                                                                            matRow.unit || ""
                                                                        ).trim();
                                                                        const rawBobbin =
                                                                            matRow.unitPriceBasis ===
                                                                            "bobbin";
                                                                        const rawNum = Number(
                                                                            matRow.priceUAH
                                                                        );
                                                                        const title = rawBobbin
                                                                            ? `На картці збережено ${Number.isFinite(rawNum) ? rawNum.toFixed(2) : "—"} ₴ за бобіну; для норми використано ₴/м (÷ намотка)`
                                                                            : u
                                                                              ? `${u}, ₴ за 1 ${u}`
                                                                              : undefined;
                                                                        return (
                                                                            <span title={title}>
                                                                                {formatMoneyUa2Fixed(
                                                                                    pu
                                                                                )}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <span className="text-muted">
                                                                            —
                                                                        </span>
                                                                    );
                                                                })()
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-1 tech-card-td-qty">
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                hiddenLabel
                                                                InputProps={{ notched: false }}
                                                                value={L.qtyPerUnit}
                                                                placeholder={
                                                                    isLinearM
                                                                        ? "м.п."
                                                                        : String(unitLbl)
                                                                }
                                                                title={
                                                                    isLinearM
                                                                        ? "Витрата тканини, м.п."
                                                                        : `Кількість на 1 шт., ${unitLbl}`
                                                                }
                                                                onChange={(e) =>
                                                                    setLines((prev) =>
                                                                        prev.map((x) =>
                                                                            x.key === L.key
                                                                                ? {
                                                                                      ...x,
                                                                                      qtyPerUnit:
                                                                                          e.target.value,
                                                                                  }
                                                                                : x
                                                                        )
                                                                    )
                                                                }
                                                                inputProps={{
                                                                    inputMode: "decimal",
                                                                    "aria-label": "Кількість",
                                                                }}
                                                                sx={TECH_CARD_MUI_FIELD_SX}
                                                            />
                                                        </td>
                                                        <td className="py-1 tech-card-td-unit">
                                                            <FormControl
                                                                size="small"
                                                                fullWidth
                                                                sx={TECH_CARD_MUI_FIELD_SX}
                                                            >
                                                                <Select
                                                                    value={L.normUnit || "stock"}
                                                                    onChange={(e) =>
                                                                        setLines((prev) =>
                                                                            prev.map((x) =>
                                                                                x.key === L.key
                                                                                    ? {
                                                                                          ...x,
                                                                                          normUnit:
                                                                                              e.target.value,
                                                                                      }
                                                                                    : x
                                                                            )
                                                                        )
                                                                    }
                                                                    displayEmpty
                                                                    input={
                                                                        <OutlinedInput notched={false} />
                                                                    }
                                                                    inputProps={{
                                                                        "aria-label": "Одиниці виміру норми",
                                                                    }}
                                                                >
                                                                    <MenuItem value="stock">
                                                                        {unitLbl}
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        value="linear_m"
                                                                        disabled={
                                                                            Boolean(matRow) &&
                                                                            !materialNeedsKgPerLinearMeter(
                                                                                matRow
                                                                            )
                                                                        }
                                                                    >
                                                                        м.п. → кг
                                                                    </MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </td>
                                                        <td className="py-1 text-end text-nowrap small tech-card-td-line-cost align-middle">
                                                            {matRow && L.materialId ? (
                                                                (() => {
                                                                    const lc = techCardEditorLineCostUAH(
                                                                        matRow,
                                                                        L
                                                                    );
                                                                    if (lc != null) {
                                                                        return (
                                                                            <span className="fw-medium">
                                                                                {formatMoneyUa2Fixed(lc)}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <span
                                                                            className="text-muted"
                                                                            title="Потрібні кількість, матеріал з ціною ₴ або для «м.п. → кг» — кг/п.м. у типі/властивостях"
                                                                        >
                                                                            —
                                                                        </span>
                                                                    );
                                                                })()
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-1 align-middle tech-card-td-actions">
                                                            <IconButton
                                                                type="button"
                                                                size="small"
                                                                title="Прибрати рядок матеріалу"
                                                                aria-label="Прибрати рядок матеріалу"
                                                                onClick={() =>
                                                                    setLines((prev) =>
                                                                        prev.length <= 1
                                                                            ? prev
                                                                            : prev.filter(
                                                                                  (x) => x.key !== L.key
                                                                              )
                                                                    )
                                                                }
                                                                sx={{
                                                                    width: 36,
                                                                    height: 36,
                                                                    padding: 0.5,
                                                                    color: "text.secondary",
                                                                    "&:hover": {
                                                                        color: "error.main",
                                                                        backgroundColor: "action.hover",
                                                                    },
                                                                }}
                                                            >
                                                                <DeleteOutline fontSize="small" />
                                                            </IconButton>
                                                        </td>
                                                    </tr>
                                                    {linearWarn ? (
                                                        <tr>
                                                            <td colSpan={7} className="pt-0 pb-1 small text-warning">
                                                                Норма «м.п. → кг» лише якщо матеріал на складі в
                                                                кілограмах. Оберіть такий матеріал або змініть
                                                                значення в «Од. вим».
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="table-light">
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="text-end fw-semibold small py-2"
                                            >
                                                Усього матеріали на 1 виріб
                                            </td>
                                            <td className="text-end fw-semibold small py-2 text-nowrap">
                                                {techCardEditorMaterialsTotalUAH != null
                                                    ? formatMoneyUa2Fixed(
                                                          techCardEditorMaterialsTotalUAH
                                                      )
                                                    : "—"}
                                            </td>
                                            <td className="py-2" aria-hidden="true" />
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                            <div className="mb-3">
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="px-3"
                                    onClick={() => setLines((prev) => [...prev, emptyLine()])}
                                    title="Додати рядок матеріалу"
                                    aria-label="Додати рядок матеріалу"
                                >
                                    +
                                </Button>
                            </div>
                        </form>
                    )}
                </Modal.Body>
                {editorActive ? (
                    <Modal.Footer className="d-flex justify-content-end border-top">
                        <Button
                            type="submit"
                            form="tech-card-editor-form"
                            variant="primary"
                            disabled={
                                saving ||
                                editorCardLoading ||
                                (Boolean(cardLoaded?._id) && !isTechCardEditorDirty)
                            }
                            title={
                                cardLoaded?._id && !isTechCardEditorDirty && !editorCardLoading
                                    ? "Спочатку внесіть зміни"
                                    : undefined
                            }
                        >
                            {saving
                                ? "Збереження…"
                                : cardLoaded?._id
                                  ? "Зберегти зміни"
                                  : "Зберегти техкарту"}
                        </Button>
                    </Modal.Footer>
                ) : null}
            </Modal>

            <HubChangeLogModal
                show={showChangeLog}
                onHide={() => setShowChangeLog(false)}
                scope="tech_card"
                title="Історія змін — техкарти"
            />
        </div>
    );
}
